import React, {useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import Page from "../../components/Page";
import {
    CustomerTypeEnum,
    InvoiceStatus,
    Order,
    OrderStatus,
    RoleIdEnum,
    useGetCurrentUserQuery,
    useGetOrderQuery,
    useDeleteOrderItemMutation,
    useRemoveInvoiceMutation,
    useRemoveOrderMutation,
    useUpdateInvoiceMutation,
    useUpdateOrderStatusMutation,
} from "../../generated/graphql";
import DefaultQueryHandler from "../../common/DefaultQueryHandler";
import {AppBar, Button, Grid, Tab, Tabs} from "@material-ui/core";
import DetailView from "../custom/DetailView";
import CreateOrderItem from "../orderItem/CreateOrderItem";
import UploadFile from "./UploadFile";
import {hasRole, HasRole, PasteToClipboard, useTabIndex, ViewColumn,} from "../../common/Util";
import CreateInvoice from "../invoice/CreateInvoice";
import ConfirmButton from "../../components/ConfirmButton";
import {
    InvoiceMethodMap,
    InvoiceStatusList,
    InvoiceStatusMap,
    OrderItemTypeMap,
    OrderStatusMap,
    PaymentDaysMap,
} from "../../common/Constant";
import TabPanel from "../custom/TabPanel";
import {format} from "date-fns";
import UpdateOrder from "./UpdateOrder";
import {DataGridLocale} from "../../common/DataGridLocale";
import {InputType} from "../../common/FormRenderer";
import CreateView from "../custom/CreateView";
import DownloadOrderContract from "./DownloadOrderContract";
import ChangeInvoice from "../invoice/ChangeInvoice";
import ChangeInvoiceDate from "../invoice/ChangeInvoiceDate";
import NotificationPopup from "../../common/NotificationPopup";
import {
    DataGridPro,
    GridColumnOrderChangeParams,
    GridColumnResizeParams,
    GridColumnVisibilityChangeParams,
    GridRenderCellParams,
    GridValueGetterParams,
} from "@mui/x-data-grid-pro";
import {useUser} from "../../Auth";

const ORDER_DETAIL_ITEMS_GRID_ID = "order-detail-items";
const ORDER_DETAIL_INVOICES_GRID_ID = "order-detail-invoices";

const readStoredJson = <T,>(key: string, fallback: T): T => {
    const stored = localStorage.getItem(key);
    if (!stored) {
        return fallback;
    }
    try {
        return JSON.parse(stored);
    } catch (e) {
        console.warn(`Failed to parse ${key}`, e);
        return fallback;
    }
};

const orderColumnsByField = <T extends { field: string }>(
    columns: T[],
    order: string[] | null
): T[] => {
    if (!order?.length) {
        return columns;
    }
    const ordered = order
        .map((field) => columns.find((column) => column.field === field))
        .filter(Boolean) as T[];
    const missing = columns.filter((column) => !order.includes(column.field));
    return [...ordered, ...missing];
};

const withPersistedColumnPrefs = <
    T extends { field: string; width?: number; minWidth?: number }
>(
    columns: T[],
    gridId: string,
    order: string[] | null,
    visibility: Record<string, boolean>,
    defaultWidth: number
): T[] => {
    return orderColumnsByField(columns, order).map((column) => {
        const storedWidth =
            localStorage.getItem(`columnWidth_${gridId}_${column.field}`) ??
            localStorage.getItem(column.field);
        return {
            ...column,
            width: Math.max(
                parseInt(
                    storedWidth ?? String(column.width ?? defaultWidth)
                ),
                column.minWidth ?? 0
            ),
            ...(typeof visibility[column.field] === "boolean"
                ? { hide: visibility[column.field] === false }
                : {}),
        };
    });
};

export default () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { data, loading, error } = useGetOrderQuery({
        variables: {
            id,
        },
    });
    const [tabIndex, setTabIndex] = useTabIndex();
    const [updateOrderStatus] = useUpdateOrderStatusMutation();
    const [updateInvoice] = useUpdateInvoiceMutation();
    const [removeOrder] = useRemoveOrderMutation();
    const [removeInvoice] = useRemoveInvoiceMutation();
    const [removeOrderItem] = useDeleteOrderItemMutation();
    const currentUserQuery = useGetCurrentUserQuery();
    const order = data?.order;
    const user = useUser();
    const [orderItemColumnOrder, setOrderItemColumnOrder] = useState<
        string[] | null
    >(() =>
        readStoredJson<string[] | null>(
            `columnsOrder_${ORDER_DETAIL_ITEMS_GRID_ID}`,
            null
        )
    );
    const [orderItemColumnVisibility, setOrderItemColumnVisibility] = useState<
        Record<string, boolean>
    >(() =>
        readStoredJson(
            `columnsVisibility_${ORDER_DETAIL_ITEMS_GRID_ID}`,
            {}
        )
    );
    const [invoiceColumnOrder, setInvoiceColumnOrder] = useState<
        string[] | null
    >(() =>
        readStoredJson<string[] | null>(
            `columnsOrder_${ORDER_DETAIL_INVOICES_GRID_ID}`,
            null
        )
    );
    const [invoiceColumnVisibility, setInvoiceColumnVisibility] = useState<
        Record<string, boolean>
    >(() =>
        readStoredJson(
            `columnsVisibility_${ORDER_DETAIL_INVOICES_GRID_ID}`,
            {}
        )
    );

    const changeStatus = async (status, changeReason = null) => {
        await updateOrderStatus({
            variables: {
                id,
                status: {
                    status,
                    changeReason,
                },
            },
            refetchQueries: ["getOrder"],
        });
    };
    const onRemoveOrderItem = async (orderItemId) => {
        try {
            await removeOrderItem({
                variables: {
                    id: orderItemId,
                },
                refetchQueries: ["getOrder"],
            });
            NotificationPopup.success(`刪除完成`);
        } catch (e) {
            NotificationPopup.error(`刪除發生問題: ${e.message}`);
            console.error(e);
        }
    };
    const cancelInvoice = async (invoiceId, note) => {
        await updateInvoice({
            variables: {
                id: invoiceId,
                invoice: {
                    status: InvoiceStatus.CancelRequested,
                    note: note,
                },
            },
            refetchQueries: ["getOrder", "getInvoiceNotification"],
        });
    }
    const updateInvoiceStatus = async (invoiceId) => {
        await updateInvoice({
            variables: {
                id: invoiceId,
                invoice: {
                    status: InvoiceStatus.CancelRequested,
                },
            },
            refetchQueries: ["getOrder"],
        });
    };
    const onRemoveOrder = async (orderId) => {
        try {
            await removeOrder({
                variables: {
                    id: orderId,
                },
                refetchQueries: ["GetOrders"],
            });
            NotificationPopup.success(`刪除完成`);
            navigate("/app/order");
        } catch (e) {
            NotificationPopup.error(`刪除發生問題: ${e.message}`);
            console.error(e);
        }
    };
    const onRemoveInvoice = async (invoiceId) => {
        try {
            await removeInvoice({
                variables: {
                    id: invoiceId,
                },
                refetchQueries: ["getOrder"],
            });
            NotificationPopup.success(`刪除完成`);
        } catch (e) {
            NotificationPopup.error(`刪除發生問題: ${e.message}`);
            console.error(e);
        }
    };
    const invoiceColumns = [
        {
            field: "id",
            headerName: "ID",
            type: "number",
        },
        {
            field: "createdAt",
            headerName: "申請時間",
            valueGetter: (params: GridValueGetterParams) => {
                return params.row.createdAt
                    ? format(
                          new Date(params.row.createdAt as string),
                          "yyyy-MM-dd HH:mm"
                      )
                    : "";
            },
            type: "datetime",
        },
        { field: "invoiceNumber", headerName: "發票號碼" },
        {
            field: "postDate",
            headerName: "開立發票日期",
            valueGetter: (params: GridValueGetterParams) => {
                return params.row.postDate
                    ? format(new Date(params.row.postDate as string), "yyyy-MM-dd")
                    : "";
            },
            type: "date",
        },
        {
            field: "creditDate",
            headerName: "收款日期",
            valueGetter: (params: GridValueGetterParams) => {
                return params.row.creditDate
                    ? format(new Date(params.row.creditDate as string), "yyyy-MM-dd")
                    : "";
            },
            type: "date",
        },
        // {
        //     field: "postDate",
        //     headerName: "回填日期",
        //     width: 150,
        //     valueGetter: (params: GridValueGetterParams) => {
        //         return params.value
        //             ? format(new Date(params.value as string), "yyyy-MM-dd")
        //             : "";
        //     },
        // },
        {
            field: "status",
            headerName: "狀態",
            valueGetter: (params: GridValueGetterParams) => {
                return InvoiceStatusMap[params.row.status as string];
            },
            valueOptions: InvoiceStatusList,
            type: "singleSelect",
        },
        ...(hasRole(user, [RoleIdEnum.Admin, RoleIdEnum.Ae, RoleIdEnum.Pm, RoleIdEnum.Finance])?[ViewColumn("/app/invoice")]:[]),
        {
            field: "action2",
            headerName: "操作",
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <>
                        {params.row.status == InvoiceStatus.Started ? (
                            <HasRole roles={[RoleIdEnum.Admin, RoleIdEnum.Ae, RoleIdEnum.Pm, RoleIdEnum.Finance]}>
                                <ConfirmButton
                                    onConfirm={async () => {
                                        await onRemoveInvoice(params.row.id);
                                    }}
                                    queryEntity={"order"}
                                    queryAction={"取消發票"}
                                    entityId={params.row.id}
                                    queryMessage={`名稱: ${params.row.name}`}
                                >
                                    取消發票
                                </ConfirmButton>
                                &nbsp;
                                <ChangeInvoiceDate invoice={params.row} />
                            </HasRole>
                        ) : null}
                        &nbsp;
                        {params.row.status == InvoiceStatus.Posted ? (
                            <HasRole roles={[RoleIdEnum.Admin, RoleIdEnum.Ae, RoleIdEnum.Pm, RoleIdEnum.Finance]}>
                            <CreateView
                                title={"請求作廢"}
                                columns={[
                                    {
                                        name: "note",
                                        label: "作廢原因",
                                        type: InputType.text,
                                    },
                                ]}
                                onSubmit={(o) => {
                                    cancelInvoice(
                                        params.row.id,
                                        o.note,
                                    );
                                    return params.row
                                }}
                                submitTitle={"申請"}
                                queryEntity={"invoice"}
                            />
                            </HasRole>
                        ) : null}
                        &nbsp;

                        <HasRole roles={[RoleIdEnum.Admin, RoleIdEnum.Ae, RoleIdEnum.Pm, RoleIdEnum.Finance]}>
                        <ChangeInvoice
                            invoice={params.row}
                            orderItems={order?.items}
                        ></ChangeInvoice>
                        </HasRole>
                    </>
                );
            },
        },
    ];
    const orderItemColumns = [
        { field: "id", headerName: "ID", type: "number" },
        {
            field: "product",
            headerName: "產品",
            valueGetter: (params: GridValueGetterParams) =>
                params.row.product?.name ?? "",
        },
        {
            field: "type",
            headerName: "操作方式",
            valueGetter: (params: GridValueGetterParams) =>
                params.row.type
                    ? OrderItemTypeMap[params.row.type as string]
                    : "未指定",
        },
        {
            field: "totalAmount",
            headerName: "購買金額",
            valueGetter: (params: GridValueGetterParams) =>
                params.row.totalAmount != null
                    ? (params.row.totalAmount as number).toLocaleString()
                    : "",
        },
        {
            field: "serviceFee",
            headerName: "帳戶執行費",
            valueGetter: (params: GridValueGetterParams) =>
                params.row.serviceFee != null
                    ? (params.row.serviceFee as number).toLocaleString()
                    : "",
        },
        {
            field: "budget",
            headerName: "成本預算",
            valueGetter: (params: GridValueGetterParams) =>
                params.row.budget != null
                    ? (params.row.budget as number).toLocaleString()
                    : "",
        },
        {
            field: "assignee",
            headerName: "專案負責人",
            valueGetter: (params: GridValueGetterParams) =>
                params.row.assignee?.name ?? "未指派",
        },
        {
            field: "supporter",
            headerName: "專案執行人",
            valueGetter: (params: GridValueGetterParams) =>
                params.row.supporter?.name ?? "未指派",
        },
        {
            field: "startDate",
            headerName: "開始時間",
            type: "date",
            valueGetter: (params: GridValueGetterParams) =>
                params.row.startDate
                    ? format(
                          new Date(params.row.startDate as string),
                          "yyyy-MM-dd"
                      )
                    : "",
        },
        {
            field: "endDate",
            headerName: "結束時間",
            type: "date",
            valueGetter: (params: GridValueGetterParams) =>
                params.row.endDate
                    ? format(
                          new Date(params.row.endDate as string),
                          "yyyy-MM-dd"
                      )
                    : "",
        },
        { field: "description", headerName: "備註" },
        {
            field: "action",
            headerName: "動作",
            width: 200,
            minWidth: 200,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() =>
                            navigate(`/app/orderItem/${params.row.id}`)
                        }
                    >
                        查看
                    </Button>
                    {order?.status == OrderStatus.Draft && (
                        <>
                            &ensp;
                            <ConfirmButton
                                onConfirm={() => {
                                    onRemoveOrderItem(params.row.id);
                                }}
                                queryEntity={"order"}
                                queryAction={"刪除委刊細項"}
                                entityId={order.id}
                                queryMessage={`名稱: ${order.name}`}
                            >
                                刪除
                            </ConfirmButton>
                        </>
                    )}
                </div>
            ),
        },
    ];
    let total = 0;
    let remainder = 0;
    order?.items?.map((item) => {
        total += item.totalAmount! + item.serviceFee!;
        remainder += item.totalAmount! + item.serviceFee!;
    });
    order?.invoices?.map((invoice) => {
        invoice.items.map((item) => {
            if (invoice.status != InvoiceStatus.Canceled) {
                total -= item.amount!;
            }
            if (invoice.status == InvoiceStatus.Paid) {
                remainder -= item.amount!;
            }
        });
    });
    const orderColumns = [
        {
            field: "status",
            headerName: "狀態",
            valueFormatter: (value) => {
                const statusString = value == 'draft' ? OrderStatusMap[value] : '進行中';
                if (value == OrderStatus.Changed) {
                    return `${statusString} -  異動原因: ${order?.changeReason}`;
                } else if (value != OrderStatus.InvoiceRequested)
                    return statusString;
                else
                    return `${statusString} / ${
                        remainder == 0 ? "已付款" : "尚未完成付款"
                    }`;
            },
        },
        { field: "name", headerName: "專案名稱" },
        { field: "number", headerName: "委刊單號" },
        {
            field: "customer",
            headerName: "客戶名稱",
            valueFormatter: (value) => {
                if (order?.customer?.type==CustomerTypeEnum.Agent ) {
                    return `${value.name} - (廣告主: ${order?.advertiser ? order?.advertiser.name : '未指定'})`;
                } else {
                    return value.name;
                }
            },
        },
        { field: "contact", headerName: "聯絡人" },
        { field: "invoiceContact", headerName: "發票聯絡人" },
        { field: "createdAt", headerName: "建立時間" },
        { field: "files", headerName: "回簽檔", removeRoles: [RoleIdEnum.Finance, RoleIdEnum.Admin, RoleIdEnum.AeManager, RoleIdEnum.PmManager, RoleIdEnum.Secretary] },
        {
            field: "invoiceMethod",
            headerName: "發票開立方法",
            valueFormatter: (value) => {
                return InvoiceMethodMap[value];
            },
        },
        {
            field: "currency",
            headerName: "幣別",
            valueFormatter: (value) => {
                return value == "TWD" ? "台幣" : "美金";
            },
        },
        { field: "paymentDays", headerName: "付款天數", valueFormatter: (value) => {return PaymentDaysMap[value]} },
        {
            field: "renewal",
            headerName: "新約/續約",
            valueFormatter: (value) => {
                return value ? "續約" : "新約";
            },
        },
        {
            field: "assignedSalesUser",
            headerName: "簽約負責人",
            valueFormatter: (user) => (user ? user.name : "無"),
        },
        {
            field: "remark",
            headerName: "合約備註",
        },
    ];

    return (
        <HasRole roles={[RoleIdEnum.Admin, RoleIdEnum.Ae, RoleIdEnum.AeManager, RoleIdEnum.Pm, RoleIdEnum.PmManager, RoleIdEnum.Gm, RoleIdEnum.Coo, RoleIdEnum.Finance, RoleIdEnum.Op, RoleIdEnum.Secretary]}>
        <Page title={"Order detail page"}>
            <AppBar position="static" color="default">
                <Tabs
                    value={tabIndex}
                    indicatorColor="secondary"
                    textColor="secondary"
                    onChange={(
                        event: React.ChangeEvent<{}>,
                        newValue: number
                    ) => {
                        setTabIndex(newValue);
                    }}
                >
                    <Tab label="委刊單" />
                    <Tab label="委刊細項" />
                    <Tab label="發票" />
                </Tabs>
            </AppBar>
            <DefaultQueryHandler loading={loading} error={error}>
                <TabPanel value={tabIndex} index={0}>
                    <Grid container spacing={3}>
                        {order?.status == OrderStatus.Draft && (
                            <Grid item>
                                <HasRole roles={[RoleIdEnum.Ae, RoleIdEnum.Pm, RoleIdEnum.Secretary, RoleIdEnum.Admin]}>
                                    <ConfirmButton
                                        onConfirm={() => {
                                            onRemoveOrder(data?.order?.id);
                                        }}
                                        queryEntity={"order"}
                                        queryAction={"刪除委刊單"}
                                        entityId={data?.order?.id}
                                        queryMessage={`名稱: ${data?.order?.name}`}
                                    >
                                        刪除
                                    </ConfirmButton>
                                </HasRole>
                            </Grid>
                        )}
                        {order?.status != OrderStatus.Finished && (
                            <Grid item>
                                <UpdateOrder order={data?.order}></UpdateOrder>
                            </Grid>
                        )}
                        {order?.status == OrderStatus.Draft && (
                            <Grid item>
                                <DownloadOrderContract order={order} />
                            </Grid>
                        )}
                        {order?.status == OrderStatus.Draft && (

                            <HasRole roles={[RoleIdEnum.Ae, RoleIdEnum.Pm, RoleIdEnum.Admin]}>
                                <Grid item>
                                    <UploadFile
                                        uriPart={`/orders/upload/${id}`}
                                        uploadTitle={"上傳回簽檔"}
                                        onSuccess={async () => {
                                            await changeStatus(order?.invoices?.length > 0 ? OrderStatus.InvoiceRequested : OrderStatus.OrderSigned);
                                        }}
                                        queryEntity={"order"}
                                        entityId={order.id}
                                    />
                                </Grid>
                            </HasRole>
                        )}
                        {order?.status != OrderStatus.Draft &&
                            order?.status != OrderStatus.Changed && (

                                <HasRole roles={[RoleIdEnum.Ae, RoleIdEnum.Pm, RoleIdEnum.Admin]}>
                                    <Grid item>
                                        <CreateView
                                            title={"異動委刊單"}
                                            columns={[
                                                {
                                                    name: "changeReason",
                                                    label: "異動原因",
                                                    type: InputType.text,
                                                },
                                            ]}
                                            onSubmit={async (o) => {
                                                await changeStatus(
                                                    OrderStatus.Changed,
                                                    o.changeReason
                                                );
                                                return order;
                                            }}
                                            submitTitle={"申請"}
                                            queryEntity={"order"}
                                        />
                                    </Grid>
                                </HasRole>
                            )}
                        {order?.status == OrderStatus.Changed && (

                            <HasRole roles={[RoleIdEnum.AeManager, RoleIdEnum.PmManager, RoleIdEnum.Gm, RoleIdEnum.Admin]}>
                                <Grid item>
                                    <CreateView
                                        title={"同意異動"}
                                        columns={[
                                            {
                                                name: "id",
                                                label: "",
                                                type: InputType.custom,
                                                renderCell: (
                                                    <div
                                                        style={{
                                                            marginBottom: 10,
                                                        }}
                                                    >
                                                        異動原因：
                                                        {order?.changeReason}
                                                    </div>
                                                ),
                                            },
                                        ]}
                                        onSubmit={async () => {
                                            await changeStatus(OrderStatus.Draft);
                                            return order;
                                        }}
                                        submitTitle={"同意"}
                                        queryEntity={"order"}
                                    />
                                </Grid>
                            </HasRole>
                        )}
                        {/* {order?.status == OrderStatus.InvoiceRequested &&
                            remainder == 0 && (
                                <Grid item>
                                    <ConfirmButton
                                        onConfirm={() => {
                                            changeStatus(OrderStatus.Finished);
                                        }}
                                    >
                                        結案
                                    </ConfirmButton>
                                </Grid>
                            )} */}
                    </Grid>
                    {/*TODO:needs some styling and layout. Upload needs to trigger a refetch for getOrder
                if they are in the same view*/}
                    <DetailView data={data?.order} columns={orderColumns} />
                </TabPanel>
                <TabPanel value={tabIndex} index={1}>
                    {order?.status != OrderStatus.Finished && (
                        <CreateOrderItem order={order} />
                    )}
                    <br/>
                    <DataGridPro
                        rows={order?.items ?? []}
                        hideFooter={true}
                        autoHeight={true}
                        localeText={DataGridLocale}
                        onCellClick={PasteToClipboard}
                        columns={withPersistedColumnPrefs(
                            orderItemColumns,
                            ORDER_DETAIL_ITEMS_GRID_ID,
                            orderItemColumnOrder,
                            orderItemColumnVisibility,
                            150
                        )}
                        onColumnWidthChange={(
                            param: GridColumnResizeParams
                        ) => {
                            window.localStorage.setItem(
                                `columnWidth_${ORDER_DETAIL_ITEMS_GRID_ID}_${param.colDef.field}`,
                                param.width.toString()
                            );
                        }}
                        onColumnOrderChange={(
                            params: GridColumnOrderChangeParams
                        ) => {
                            const fields = orderColumnsByField(
                                orderItemColumns,
                                orderItemColumnOrder
                            ).map((column) => column.field);
                            const next = [...fields];
                            const [moved] = next.splice(params.oldIndex, 1);
                            next.splice(params.targetIndex, 0, moved);
                            setOrderItemColumnOrder(next);
                            localStorage.setItem(
                                `columnsOrder_${ORDER_DETAIL_ITEMS_GRID_ID}`,
                                JSON.stringify(next)
                            );
                        }}
                        onColumnVisibilityChange={(
                            params: GridColumnVisibilityChangeParams
                        ) => {
                            setOrderItemColumnVisibility((prev) => {
                                const next = {
                                    ...prev,
                                    [params.field]: params.isVisible,
                                };
                                localStorage.setItem(
                                    `columnsVisibility_${ORDER_DETAIL_ITEMS_GRID_ID}`,
                                    JSON.stringify(next)
                                );
                                return next;
                            });
                        }}
                    />
                </TabPanel>
                <TabPanel value={tabIndex} index={2}>
                    <div>
                        {(order?.status == OrderStatus.OrderSigned ||
                            order?.status == OrderStatus.InvoiceRequested) && (

                            <HasRole roles={[RoleIdEnum.Admin, RoleIdEnum.Ae, RoleIdEnum.Pm, RoleIdEnum.Finance]}>
                            <CreateInvoice
                                order={order as Order}
                                total={total}
                            />
                            </HasRole>
                        )}
                        <div style={{ marginTop: 10, marginBottom: 10 }}>
                            總剩餘可開發票的金額：{total.toLocaleString()}
                        </div>
                        <DataGridPro
                            rows={data?.order?.invoices!}
                            hideFooter={true}
                            autoHeight={true}
                            localeText={DataGridLocale}
                            onCellClick={PasteToClipboard}
                            columns={withPersistedColumnPrefs(
                                invoiceColumns,
                                ORDER_DETAIL_INVOICES_GRID_ID,
                                invoiceColumnOrder,
                                invoiceColumnVisibility,
                                200
                            )}
                            onColumnWidthChange={(param: GridColumnResizeParams) => {
                                window.localStorage.setItem(
                                    `columnWidth_${ORDER_DETAIL_INVOICES_GRID_ID}_${param.colDef.field}`,
                                    param.width.toString()
                                );
                            }}
                            onColumnOrderChange={(
                                params: GridColumnOrderChangeParams
                            ) => {
                                const fields = orderColumnsByField(
                                    invoiceColumns,
                                    invoiceColumnOrder
                                ).map((column) => column.field);
                                const next = [...fields];
                                const [moved] = next.splice(params.oldIndex, 1);
                                next.splice(params.targetIndex, 0, moved);
                                setInvoiceColumnOrder(next);
                                localStorage.setItem(
                                    `columnsOrder_${ORDER_DETAIL_INVOICES_GRID_ID}`,
                                    JSON.stringify(next)
                                );
                            }}
                            onColumnVisibilityChange={(
                                params: GridColumnVisibilityChangeParams
                            ) => {
                                setInvoiceColumnVisibility((prev) => {
                                    const next = {
                                        ...prev,
                                        [params.field]: params.isVisible,
                                    };
                                    localStorage.setItem(
                                        `columnsVisibility_${ORDER_DETAIL_INVOICES_GRID_ID}`,
                                        JSON.stringify(next)
                                    );
                                    return next;
                                });
                            }}
                        />
                    </div>
                </TabPanel>
            </DefaultQueryHandler>
        </Page>
        </HasRole>
    );
};
